import React from 'react';
import { useDebounce } from 'use-debounce';
import { useHistory, useLocation } from 'react-router-dom';
import { 
  encodeFiltersToURL, 
  decodeFiltersFromURL, 
  validateURLSize,
  createShareableURL,
  hasActiveFilters
} from 'lib/utils/urlFilterUtils';

const useSetFilters = () => {
  const history = useHistory();
  const location = useLocation();
  
  const initialValues = {
    sort: 'RISK',
    startAge: 0,
    endAge: 130,
    risk: [],
    gender: [],
    condition: [],
    patientGroupIds: [],
    careTeamIds: [],
    activeRequestGroupPlanDefinitionIds: null,
    completedRequestGroupPlanDefinitionIds: null,
    hasNoRequestGroups: undefined,
    // startGlobalEngagement: 0,
    // endGlobalEngagement: 100,
  };

  // SessionStorage key for persisting filters
  const STORAGE_KEY = 'patientListFilters';

  // Check if URL has any filter parameters
  const hasFiltersInURL = (searchParams) => {
    const filterParams = ['sort', 'startAge', 'endAge', 'name', 'risk', 'gender', 'condition', 'patientGroupIds', 'careTeamIds', 'activity', 'hasActiveAlert', 'hasNoActiveAlerts', 'activeCarePlans', 'completedCarePlans', 'hasNoCarePlans'];
    return filterParams.some(param => searchParams.has(param));
  };

  // Load saved filters from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that saved filters are reasonable (not corrupted)
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to parse saved patient filters:', e);
    }
    return null;
  };

  // Initialize filters with smart priority logic
  const getInitialFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    
    // Priority 1: URL parameters (for sharing/bookmarking)
    if (hasFiltersInURL(searchParams)) {
      const urlFilters = decodeFiltersFromURL(searchParams);
      return { ...initialValues, ...urlFilters };
    }
    
    // Priority 2: Saved filters from sessionStorage (for navigation persistence)
    const savedFilters = getSavedFilters();
    if (savedFilters) {
      return { ...initialValues, ...savedFilters };
    }
    
    // Priority 3: Default values (clean start)
    return initialValues;
  };

  // This is for displaying accurate filters values for user
  const [filters, setFilters] = React.useState(getInitialFilters);
  // Sanitize filters for API calls (remove internal fields)
  const sanitizeFiltersForAPI = (filters) => {
    const { _activityKey, ...apiFilters } = filters;
    return apiFilters;
  };

  // This one is used for requests
  const [debouncedFiltersRaw] = useDebounce(filters, 500);
  const debouncedFilters = sanitizeFiltersForAPI(debouncedFiltersRaw);

  // Track if we should update URL (avoid infinite loops)
  const [shouldUpdateURL, setShouldUpdateURL] = React.useState(true);

  // Effect to sync filters to URL (debounced to match API calls)
  React.useEffect(() => {
    if (!shouldUpdateURL) return;

    const params = encodeFiltersToURL(filters);
    
    // Only update URL if it's within size limits
    if (validateURLSize(params)) {
      const newSearch = params.toString();
      const currentSearch = location.search.substring(1); // Remove '?'
      
      // Only update URL if search params have actually changed
      if (newSearch !== currentSearch) {
        const newURL = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        history.replace(newURL);
      }
    } else {
      console.warn('Filter URL parameters exceed size limit, not updating URL');
    }
  }, [debouncedFilters, history, location.pathname, shouldUpdateURL]);

  // Auto-save filters to sessionStorage when they change
  React.useEffect(() => {
    // Save filters to sessionStorage if they differ from defaults
    if (hasActiveFilters(filters, initialValues)) {
      try {
        // Remove internal fields before saving
        const { _activityKey, ...filtersToSave } = filters;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filtersToSave));
      } catch (e) {
        console.warn('Failed to save patient filters to sessionStorage:', e);
      }
    } else {
      // Clear saved filters when back to defaults
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        console.warn('Failed to clear saved patient filters:', e);
      }
    }
  }, [filters]);

  // Effect to handle browser navigation (back/forward buttons)
  React.useEffect(() => {
    const handlePopState = () => {
      setShouldUpdateURL(false);
      const searchParams = new URLSearchParams(window.location.search);
      const urlFilters = decodeFiltersFromURL(searchParams);
      setFilters({ ...initialValues, ...urlFilters });
      
      // Re-enable URL updates after state has been set
      setTimeout(() => setShouldUpdateURL(true), 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [initialValues]);


  const resetFilters = () => {
    // Clear sessionStorage
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear saved patient filters:', e);
    }
    
    // Reset filters to initial values (this will also clear URL via the effect)
    setFilters(initialValues);
  };

  const handleSearch = (name) => {
    setFilters((curr) => ({
      ...curr,
      name,
    }));
  };

  // Helper functions for external use
  const getShareableURL = () => createShareableURL(filters);
  const hasFiltersActive = () => hasActiveFilters(filters, initialValues);
  
  // Determine which filter types are active for accordion state management
  const getActiveFilterTypes = () => {
    const activeTypes = {};
    
    // Check each filter type for active state
    if (filters.gender && filters.gender.length > 0) {
      activeTypes.gender = true;
    }
    if (filters.startAge !== 0 || filters.endAge !== 130) {
      activeTypes.age = true;
    }
    if (filters.lastActivity || filters.noActivity) {
      activeTypes.activity = true;
    }
    if (filters.risk && filters.risk.length > 0) {
      activeTypes.risk = true;
    }
    if (filters.condition && filters.condition.length > 0) {
      activeTypes.condition = true;
    }
    if (filters.careTeamIds && filters.careTeamIds.length > 0) {
      activeTypes.careTeam = true;
    }
    if (filters.patientGroupIds && filters.patientGroupIds.length > 0) {
      activeTypes.patientGroup = true;
    }
    if (filters.hasActiveAlert || filters.hasNoActiveAlerts) {
      activeTypes.alerts = true;
    }
    if (Array.isArray(filters.activeRequestGroupPlanDefinitionIds) || 
        Array.isArray(filters.completedRequestGroupPlanDefinitionIds) || 
        filters.hasNoRequestGroups) {
      activeTypes.carePlan = true;
    }
    
    return activeTypes;
  };

  return {
    filters,
    debouncedFilters,
    resetFilters,
    setFilters,
    handleSearch,
    getShareableURL,
    hasFiltersActive,
    getActiveFilterTypes,
  };
};

export default useSetFilters;
